/**
 * 폴더 컨텍스트
 * 폴더 상태 관리 및 비즈니스 로직
 */

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useAuth } from './AuthContext';
import {
  createFolder,
  getFolders,
  updateFolder,
  deleteFolder,
  buildFolderTree,
  moveNoteToFolder,
  moveNotesToFolder,
  updateFolderNoteCount,
  SYSTEM_FOLDERS,
  SYSTEM_FOLDER_INFO
} from '../services/folderService';

const FolderContext = createContext();

export function useFolders() {
  const context = useContext(FolderContext);
  if (!context) {
    throw new Error('useFolders must be used within FolderProvider');
  }
  return context;
}

export function FolderProvider({ children }) {
  const { user } = useAuth();
  const [folders, setFolders] = useState([]);
  const [folderTree, setFolderTree] = useState(() => {
    // 초기 상태에서 시스템 폴더 포함
    return buildFolderTree([]);
  });
  const [selectedFolder, setSelectedFolder] = useState(SYSTEM_FOLDERS.ALL);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // 폴더 목록 로드
  const loadFolders = useCallback(async () => {
    if (!user) return;

    setLoading(true);
    setError(null);

    try {
      const userFolders = await getFolders(user.uid);
      setFolders(userFolders);

      // 트리 구조 생성
      const tree = buildFolderTree(userFolders);
      setFolderTree(tree);
    } catch (err) {
      console.error('Error loading folders:', err);
      setError('폴더를 불러오는데 실패했습니다.');
    } finally {
      setLoading(false);
    }
  }, [user]);

  // 초기 로드
  useEffect(() => {
    if (user) {
      loadFolders();
    } else {
      // 사용자가 없어도 시스템 폴더는 표시
      setFolderTree(buildFolderTree([]));
    }
  }, [user, loadFolders]);

  // 폴더 생성
  const createNewFolder = async (folderData) => {
    if (!user) throw new Error('User not authenticated');

    setLoading(true);
    setError(null);

    try {
      const newFolder = await createFolder(user.uid, folderData);

      // 로컬 상태 업데이트
      const updatedFolders = [...folders, newFolder];
      setFolders(updatedFolders);

      // 트리 재구성
      const tree = buildFolderTree(updatedFolders);
      setFolderTree(tree);

      return newFolder;
    } catch (err) {
      console.error('Error creating folder:', err);
      setError('폴더 생성에 실패했습니다.');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // 폴더 수정
  const updateExistingFolder = async (folderId, updates) => {
    if (!user) throw new Error('User not authenticated');

    setLoading(true);
    setError(null);

    try {
      const updatedFolder = await updateFolder(user.uid, folderId, updates);

      // 로컬 상태 업데이트
      const updatedFolders = folders.map(f =>
        f.id === folderId ? { ...f, ...updatedFolder } : f
      );
      setFolders(updatedFolders);

      // 트리 재구성
      const tree = buildFolderTree(updatedFolders);
      setFolderTree(tree);

      return updatedFolder;
    } catch (err) {
      console.error('Error updating folder:', err);
      setError('폴더 수정에 실패했습니다.');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // 폴더 삭제
  const deleteExistingFolder = async (folderId, moveNotesTo = SYSTEM_FOLDERS.ALL) => {
    if (!user) throw new Error('User not authenticated');

    setLoading(true);
    setError(null);

    try {
      await deleteFolder(user.uid, folderId, moveNotesTo);

      // 로컬 상태 업데이트
      const updatedFolders = folders.filter(f => f.id !== folderId);
      setFolders(updatedFolders);

      // 트리 재구성
      const tree = buildFolderTree(updatedFolders);
      setFolderTree(tree);

      // 선택된 폴더가 삭제된 경우 기본 폴더로 변경
      if (selectedFolder === folderId) {
        setSelectedFolder(SYSTEM_FOLDERS.ALL);
      }

      return true;
    } catch (err) {
      console.error('Error deleting folder:', err);
      setError('폴더 삭제에 실패했습니다.');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // 메모를 폴더로 이동
  const moveNote = async (noteId, targetFolderId) => {
    if (!user) throw new Error('User not authenticated');

    try {
      await moveNoteToFolder(noteId, targetFolderId);

      // 폴더별 메모 개수 업데이트
      if (targetFolderId && targetFolderId !== SYSTEM_FOLDERS.ALL) {
        await updateFolderNoteCount(user.uid, targetFolderId);
      }

      return true;
    } catch (err) {
      console.error('Error moving note:', err);
      setError('메모 이동에 실패했습니다.');
      throw err;
    }
  };

  // 여러 메모를 폴더로 이동
  const moveNotes = async (noteIds, targetFolderId) => {
    if (!user) throw new Error('User not authenticated');

    try {
      await moveNotesToFolder(noteIds, targetFolderId);

      // 폴더별 메모 개수 업데이트
      if (targetFolderId && targetFolderId !== SYSTEM_FOLDERS.ALL) {
        await updateFolderNoteCount(user.uid, targetFolderId);
      }

      return true;
    } catch (err) {
      console.error('Error moving notes:', err);
      setError('메모 이동에 실패했습니다.');
      throw err;
    }
  };

  // 폴더 선택
  const selectFolder = useCallback((folderId) => {
    setSelectedFolder(folderId);
  }, []);

  // 폴더 정보 가져오기
  const getFolderInfo = (folderId) => {
    // 시스템 폴더인 경우
    if (SYSTEM_FOLDER_INFO[folderId]) {
      return SYSTEM_FOLDER_INFO[folderId];
    }

    // 사용자 폴더인 경우
    const folder = folders.find(f => f.id === folderId);
    return folder || null;
  };

  // 폴더 경로 가져오기 (breadcrumb용)
  const getFolderPath = (folderId) => {
    const path = [];
    let currentFolder = folders.find(f => f.id === folderId);

    while (currentFolder) {
      path.unshift(currentFolder);
      if (currentFolder.parentId) {
        currentFolder = folders.find(f => f.id === currentFolder.parentId);
      } else {
        break;
      }
    }

    return path;
  };

  const value = {
    folders,
    folderTree,
    selectedFolder,
    loading,
    error,
    createFolder: createNewFolder,
    updateFolder: updateExistingFolder,
    deleteFolder: deleteExistingFolder,
    moveNote,
    moveNotes,
    selectFolder,
    getFolderInfo,
    getFolderPath,
    reloadFolders: loadFolders,
    SYSTEM_FOLDERS,
    SYSTEM_FOLDER_INFO
  };

  return (
    <FolderContext.Provider value={value}>
      {children}
    </FolderContext.Provider>
  );
}

export default FolderContext;